import {
  EMR_PAGE_SOURCE_LABELS,
  type EmrPageChartImportInput,
  type EmrPageSource,
  ImportAllergySchema,
  ImportFamilyHistoryRowSchema,
  ImportImmunizationSchema,
  ImportLabResultSchema,
  ImportLegacyVisitSchema,
  ImportMedicationSchema,
  ImportPmhSchema,
  ImportSocialHistorySchema,
  type IncomingDocumentMimeSchema,
  type IncomingReportCategorySchema,
  type PatientAdministrativeSexSchema,
} from "@emr/contracts";
import type { z } from "zod";
import { SOCIAL_KIND_OPTIONS } from "../components/patient-profile/sectionEditors";

export type PatientAdministrativeSex = z.infer<typeof PatientAdministrativeSexSchema>;

/**
 * What a chart page parser found, before the physician reviews it.
 *
 * Source-neutral: each EMR's parser fills it from its own page. Chart rows use
 * the chart importer's row shapes minus the source reference, which is assigned
 * by position when the reviewed rows are saved. Anything the page showed that
 * BobDoc has no place for goes to `notImported`, as the page labelled it, so the
 * physician sees it rather than losing it silently.
 */
export interface ExtractedChartPage {
  readonly source: EmrPageSource;
  readonly demographics: ExtractedDemographics;
  readonly healthCard?: ExtractedHealthCard;
  readonly medications: readonly ExtractedMedication[];
  readonly allergies: readonly ExtractedAllergy[];
  /** Medical and surgical history (`category` tells them apart). */
  readonly problems: readonly ExtractedProblem[];
  readonly familyHistory: readonly ExtractedFamilyHistory[];
  readonly socialHistory: readonly ExtractedSocialHistory[];
  readonly immunizations: readonly ExtractedImmunization[];
  readonly labs: readonly ExtractedLabResult[];
  /** Past visits, saved as legacy visits: the previous clinic's record, never an encounter here. */
  readonly visits: readonly ExtractedVisit[];
  /**
   * The patient's files, when the bookmark brought them (a page alone only
   * lists them, under `notImported`). Filed as migrated documents.
   */
  readonly files: readonly ExtractedFile[];
  readonly notImported: readonly NotImportedItem[];
}

export interface ExtractedDemographics {
  readonly legalFirstName?: string;
  readonly legalMiddleName?: string;
  readonly legalLastName?: string;
  readonly preferredName?: string;
  /** `YYYY-MM-DD`. */
  readonly dateOfBirth?: string;
  readonly administrativeSex?: PatientAdministrativeSex;
  readonly phone?: string;
  readonly email?: string;
  readonly address?: ExtractedAddress;
  readonly emergencyContact?: ExtractedEmergencyContact;
}

// Type aliases, not interfaces, so they fit the profile contract's open address shape.
export type ExtractedAddress = {
  readonly line1?: string;
  readonly line2?: string;
  readonly city?: string;
  readonly region?: string;
  readonly postalCode?: string;
  readonly country?: string;
};

export type ExtractedEmergencyContact = {
  readonly name?: string;
  readonly relationship?: string;
  readonly phone?: string;
};

export interface ExtractedHealthCard {
  /** Digits only. */
  readonly number: string;
  readonly versionCode?: string;
  /** `YYYY-MM-DD`. */
  readonly expiryDate?: string;
  /** Two-letter province code; Ontario when the page does not say. */
  readonly province?: string;
}

type Row<Schema extends z.ZodType> = Omit<z.infer<Schema>, "sourceReference">;
export type ExtractedMedication = Row<typeof ImportMedicationSchema>;
export type ExtractedAllergy = Row<typeof ImportAllergySchema>;
export type ExtractedProblem = Row<typeof ImportPmhSchema>;
export type ExtractedFamilyHistory = Row<typeof ImportFamilyHistoryRowSchema>;
export type ExtractedSocialHistory = Row<typeof ImportSocialHistorySchema>;
export type ExtractedImmunization = Row<typeof ImportImmunizationSchema>;
export type ExtractedVisit = Row<typeof ImportLegacyVisitSchema>;

export interface ExtractedLabResult {
  /** `YYYY-MM-DD`, or a date-time with an offset. */
  readonly collectedOn: string;
  readonly panelName?: string;
  /** The laboratory, when the page names it. */
  readonly labName?: string;
  readonly result: Omit<z.infer<typeof ImportLabResultSchema>, "sourceReference" | "collectedAt">;
}

/**
 * A file from the other EMR, filed on the new chart as a migrated document:
 * the bytes as they were, under the other EMR's own title, date and notes.
 */
export interface ExtractedFile {
  /** The other EMR's own reference for it, kept with the document (`file:<id>`). */
  readonly reference: string;
  readonly title: string;
  readonly fileName: string;
  readonly mimeType: z.infer<typeof IncomingDocumentMimeSchema>;
  readonly bytes: ArrayBuffer;
  /** `YYYY-MM-DD`: the day the other EMR filed it under. */
  readonly effectiveDate?: string;
  /** When the other clinic received it, with an offset. */
  readonly receivedAt?: string;
  /** When someone there last reviewed it, with an offset. */
  readonly reviewedAt?: string;
  readonly notes?: string;
  readonly categoryHint?: z.infer<typeof IncomingReportCategorySchema>;
  /** Archived in the other EMR: still brought, and flagged in the review. */
  readonly archived?: boolean;
}

export interface NotImportedItem {
  /** Where on the page it was, e.g. "Health Profile". */
  readonly section: string;
  readonly label: string;
  readonly value: string;
  /**
   * A file the page listed by name only. When the bookmark brings the
   * patient's files themselves, these give way to them.
   */
  readonly listedFile?: true;
}

export const CHART_SECTIONS = [
  "medications",
  "allergies",
  "problems",
  "familyHistory",
  "socialHistory",
  "immunizations",
  "labs",
  "visits",
  "files",
] as const;
export type ChartSection = (typeof CHART_SECTIONS)[number];
/** The sections saved as the chart importer's rows, one row per entry. */
type RowSection = Exclude<ChartSection, "labs" | "files">;

export const CHART_SECTION_LABELS: Readonly<Record<ChartSection, string>> = {
  medications: "Medications",
  allergies: "Allergies",
  problems: "Medical and surgical history",
  familyHistory: "Family history",
  socialHistory: "Social history",
  immunizations: "Immunizations",
  labs: "Lab results",
  visits: "Past visits",
  files: "Files",
};

const ROW_SCHEMAS = {
  medications: ImportMedicationSchema,
  allergies: ImportAllergySchema,
  problems: ImportPmhSchema,
  familyHistory: ImportFamilyHistoryRowSchema,
  socialHistory: ImportSocialHistorySchema,
  immunizations: ImportImmunizationSchema,
  visits: ImportLegacyVisitSchema,
} as const;

/** The review and save key of a chart row: its section and its position on the page. */
export function chartItemKey(section: ChartSection, index: number): string {
  return `${section}:${index}`;
}

const SOURCE_REFERENCE_PREFIX: Readonly<Record<Exclude<ChartSection, "files">, string>> = {
  medications: "medication",
  allergies: "allergy",
  problems: "problem",
  familyHistory: "family",
  socialHistory: "social",
  immunizations: "immunization",
  labs: "lab",
  visits: "visit",
};

function sourceReference(section: Exclude<ChartSection, "files">, index: number): string {
  return `${SOURCE_REFERENCE_PREFIX[section]}:${index}`;
}

/**
 * Keeps the rows the chart importer will accept and moves the rest to "not
 * imported", labelled with their section and with what the page said, so a
 * row a parser read but could not shape (an immunization with only a year, a
 * relative BobDoc has no relation for) is shown rather than dropped.
 */
export function checkedChartPage(page: ExtractedChartPage): ExtractedChartPage {
  const notImported: NotImportedItem[] = [...page.notImported];
  const keep = <T extends object>(
    section: RowSection,
    rows: readonly T[],
    said: (row: T) => string | undefined,
  ): T[] =>
    rows.filter((row, index) => {
      const valid = ROW_SCHEMAS[section].safeParse({
        ...row,
        sourceReference: sourceReference(section, index),
      }).success;
      if (!valid) {
        notImported.push({
          section: CHART_SECTION_LABELS[section],
          label: "Could not be read as an entry",
          value: said(row) ?? "",
        });
      }
      return valid;
    });
  const named = (row: { readonly display?: string }) => row.display;
  const medications = keep("medications", page.medications, named);
  const allergies = keep("allergies", page.allergies, named);
  const problems = keep("problems", page.problems, named);
  const familyHistory = keep("familyHistory", page.familyHistory, named);
  const socialHistory = keep("socialHistory", page.socialHistory, (row) => row.detail);
  const immunizations = keep("immunizations", page.immunizations, named);
  const visits = keep("visits", page.visits, (row) =>
    [row.occurredAt.slice(0, 10), row.reason].filter(Boolean).join(" · "),
  );
  const labs = page.labs.filter((lab) => {
    const valid =
      collectedAtFor(lab.collectedOn) !== null &&
      ImportLabResultSchema.safeParse({ ...lab.result, sourceReference: "lab:0" }).success;
    if (!valid) {
      notImported.push({
        section: CHART_SECTION_LABELS.labs,
        label: lab.collectedOn || "Undated result",
        value: [
          lab.result.display,
          lab.result.valueNumeric ?? lab.result.valueText,
          lab.result.unit,
        ]
          .filter(Boolean)
          .join(" "),
      });
    }
    return valid;
  });
  return {
    ...page,
    medications,
    allergies,
    problems,
    familyHistory,
    socialHistory,
    immunizations,
    labs,
    visits,
    notImported: notImported.filter((item) => item.value.trim().length > 0),
  };
}

/**
 * What the bookmark brought of the patient's files, as the EMR's parser read
 * them: the files, and what it left behind with why; or why there are none.
 */
export type BroughtFiles =
  | { readonly files: readonly ExtractedFile[]; readonly leftBehind: readonly NotImportedItem[] }
  | { readonly failed: string };

/**
 * The page with the patient's files. Brought files take the place of the names
 * the page listed, and what the bookmark left behind is listed with why. When
 * there are none to bring, the listed names stay and the reason joins them.
 */
export function withBroughtFiles(
  page: ExtractedChartPage,
  brought: BroughtFiles,
): ExtractedChartPage {
  if ("failed" in brought) {
    const section = page.notImported.find((item) => item.listedFile)?.section ?? "Files";
    return {
      ...page,
      notImported: [
        ...page.notImported,
        { section, label: "Files not brought", value: brought.failed },
      ],
    };
  }
  return {
    ...page,
    files: brought.files,
    notImported: [
      ...page.notImported.filter((item) => item.listedFile !== true),
      ...brought.leftBehind,
    ],
  };
}

// ---------------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------------

export interface ReviewItem {
  readonly key: string;
  readonly section: ChartSection;
  readonly title: string;
  readonly detail?: string;
  /** A status worth flagging, e.g. "Stopped" or "Resolved"; absent when current. */
  readonly status?: string;
}

const MEDICATION_STATUS: Readonly<Record<string, string>> = {
  inactive: "Stopped",
  completed: "Completed",
  unknown: "Status unknown",
};
const RESOLVED_STATUS: Readonly<Record<string, string>> = {
  inactive: "Inactive",
  resolved: "Resolved",
};
const SOCIAL_STATUS: Readonly<Record<string, string>> = {
  current: "Current",
  former: "Former",
  never: "Never",
};

function humanize(value: string): string {
  const words = value.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function socialKind(kind: string): string {
  return SOCIAL_KIND_OPTIONS.find((option) => option.value === kind)?.label ?? humanize(kind);
}

function joined(parts: ReadonlyArray<string | undefined>): string | undefined {
  const text = parts.filter((part) => part !== undefined && part.trim() !== "").join(" · ");
  return text === "" ? undefined : text;
}

function present<T>(value: T | undefined): value is T {
  return value !== undefined;
}

function reviewItem(
  section: ChartSection,
  index: number,
  title: string,
  detail: string | undefined,
  status?: string,
): ReviewItem {
  return {
    key: chartItemKey(section, index),
    section,
    title,
    ...(detail === undefined ? {} : { detail }),
    ...(status === undefined ? {} : { status }),
  };
}

/** One row per chart entry the physician can keep or leave out, in page order. */
export function reviewItems(page: ExtractedChartPage): ReviewItem[] {
  return [
    ...page.medications.map((row, index) =>
      reviewItem(
        "medications",
        index,
        row.display,
        row.sig,
        row.status === undefined ? undefined : MEDICATION_STATUS[row.status],
      ),
    ),
    ...page.allergies.map((row, index) =>
      reviewItem(
        "allergies",
        index,
        row.display,
        joined([row.reaction, row.severity === undefined ? undefined : humanize(row.severity)]),
        row.status === undefined ? undefined : RESOLVED_STATUS[row.status],
      ),
    ),
    ...page.problems.map((row, index) =>
      reviewItem(
        "problems",
        index,
        row.display,
        joined([
          row.category === "surgical" ? "Surgical" : undefined,
          // A surgery's date is the day it was done, not an onset.
          row.onsetDate === undefined
            ? undefined
            : row.category === "surgical"
              ? row.onsetDate
              : `Since ${row.onsetDate}`,
          row.note,
        ]),
        row.status === undefined ? undefined : RESOLVED_STATUS[row.status],
      ),
    ),
    ...page.familyHistory.map((row, index) =>
      reviewItem(
        "familyHistory",
        index,
        // "Other" says nothing; the page's own words name the relative.
        row.relation === "other" ? row.display : `${humanize(row.relation)}: ${row.display}`,
        joined([
          row.onsetAge === undefined ? undefined : `Onset age ${row.onsetAge}`,
          row.deceased === true ? "Deceased" : undefined,
          row.note,
        ]),
      ),
    ),
    ...page.socialHistory.map((row, index) =>
      reviewItem(
        "socialHistory",
        index,
        // The page may already have led with the kind ("Alcohol: 2 drinks per week").
        row.detail.toLowerCase().startsWith(socialKind(row.kind).toLowerCase())
          ? row.detail
          : `${socialKind(row.kind)}: ${row.detail}`,
        joined([SOCIAL_STATUS[row.status], row.quantity, row.frequency, row.note]),
      ),
    ),
    ...page.immunizations.map((row, index) =>
      reviewItem(
        "immunizations",
        index,
        row.display,
        joined([
          row.administeredOn,
          row.doseNumber === undefined ? undefined : `Dose ${row.doseNumber}`,
        ]),
      ),
    ),
    ...page.labs.map((row, index) =>
      reviewItem(
        "labs",
        index,
        row.result.display,
        joined([
          [row.result.valueNumeric ?? row.result.valueText, row.result.unit]
            .filter(Boolean)
            .join(" "),
          row.result.flag,
          row.collectedOn.slice(0, 10),
        ]),
      ),
    ),
    ...page.visits.map((row, index) =>
      reviewItem(
        "visits",
        index,
        [row.occurredAt.slice(0, 10), row.reason].filter(Boolean).join(" · "),
        joined([
          row.note?.authorDisplay ?? row.provider?.display,
          row.note === undefined ? "No note" : excerpt(row.note.text),
        ]),
        row.note !== undefined && row.noteSignedInSource !== true ? "Not signed" : undefined,
      ),
    ),
    ...page.files.map((file, index) =>
      reviewItem(
        "files",
        index,
        file.title,
        joined([
          file.effectiveDate,
          FILE_KINDS[file.mimeType],
          fileSize(file.bytes.byteLength),
          file.notes === undefined ? undefined : excerpt(file.notes),
        ]),
        file.archived === true ? "Archived" : undefined,
      ),
    ),
  ];
}

const FILE_KINDS: Readonly<Record<ExtractedFile["mimeType"], string>> = {
  "application/pdf": "PDF",
  "image/jpeg": "JPEG image",
  "image/png": "PNG image",
};

/** A file's size the way a person reads it: "820 KB", "2.4 MB". */
export function fileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  const megabytes = bytes / (1024 * 1024);
  return `${megabytes < 10 ? megabytes.toFixed(1) : Math.round(megabytes)} MB`;
}

/** A note's opening, on one line. */
function excerpt(text: string): string {
  const line = text.replace(/\s+/g, " ").trim();
  return line.length > 120 ? `${line.slice(0, 119)}…` : line;
}

// ---------------------------------------------------------------------------
// Saving
// ---------------------------------------------------------------------------

/**
 * A date-time for a result the page dated without a time: noon in Ontario, so
 * the day it shows never moves with the reader's time zone. Null when the date
 * cannot be read.
 */
export function collectedAtFor(collectedOn: string): string | null {
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?([+-]\d{2}:\d{2}|Z)$/.test(collectedOn)) {
    return Number.isNaN(Date.parse(collectedOn)) ? null : collectedOn;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(collectedOn)) return null;
  const noon = new Date(`${collectedOn}T12:00:00Z`);
  if (Number.isNaN(noon.getTime()) || noon.toISOString().slice(0, 10) !== collectedOn) return null;
  const zone = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Toronto",
    timeZoneName: "longOffset",
  })
    .formatToParts(noon)
    .find((part) => part.type === "timeZoneName")?.value;
  const offset = zone?.match(/[+-]\d{2}:\d{2}/)?.[0] ?? "-05:00";
  return `${collectedOn}T12:00:00${offset}`;
}

/**
 * The chart import for the rows the physician kept, each with its positional
 * source reference. Lab results taken together (same date and panel) become
 * one accession, as a laboratory report would be.
 */
export function chartImportInput(
  page: ExtractedChartPage,
  kept: ReadonlySet<string>,
): EmrPageChartImportInput {
  const rows = <T extends object>(section: RowSection, list: readonly T[]) =>
    list
      .map((row, index) =>
        kept.has(chartItemKey(section, index))
          ? { ...row, sourceReference: sourceReference(section, index) }
          : undefined,
      )
      .filter(present);

  const accessions = new Map<
    string,
    {
      collectedAt: string;
      panelName?: string;
      results: Array<z.infer<typeof ImportLabResultSchema>>;
    }
  >();
  const labNames = new Set<string>();
  page.labs.forEach((lab, index) => {
    const collectedAt = collectedAtFor(lab.collectedOn);
    if (!kept.has(chartItemKey("labs", index)) || collectedAt === null) return;
    if (lab.labName !== undefined) labNames.add(lab.labName);
    const group = `${collectedAt}\n${lab.panelName ?? ""}`;
    const accession = accessions.get(group) ?? {
      collectedAt,
      ...(lab.panelName === undefined ? {} : { panelName: lab.panelName }),
      results: [],
    };
    accession.results.push({ ...lab.result, sourceReference: sourceReference("labs", index) });
    accessions.set(group, accession);
  });

  const input: EmrPageChartImportInput = {
    source: page.source,
    medications: rows("medications", page.medications),
    allergies: rows("allergies", page.allergies),
    pmh: rows("problems", page.problems),
    familyHistory: rows("familyHistory", page.familyHistory),
    socialHistory: rows("socialHistory", page.socialHistory),
    immunizations: rows("immunizations", page.immunizations),
    visits: rows("visits", page.visits),
  };
  if (accessions.size === 0) return input;
  return {
    ...input,
    labs: {
      // One laboratory named on every kept result names the import; otherwise the EMR does.
      labName:
        (labNames.size === 1 ? [...labNames][0] : undefined) ??
        `Imported from ${EMR_PAGE_SOURCE_LABELS[page.source]}`,
      accessions: [...accessions.values()].map((accession, index) => ({
        accessionNumber: `page-${index + 1}`,
        collectedAt: accession.collectedAt,
        ...(accession.panelName === undefined ? {} : { panelName: accession.panelName }),
        summary: accession.results
          .map((result) =>
            [
              result.display,
              [result.valueNumeric ?? result.valueText, result.unit].filter(Boolean).join(" "),
              result.flag === undefined ? undefined : `(${result.flag})`,
            ]
              .filter(Boolean)
              .join(" "),
          )
          .join("; ")
          .slice(0, 2000),
        results: accession.results,
      })),
    },
  };
}

/** The files the physician kept, to be filed once the patient is registered. */
export function keptFiles(
  page: ExtractedChartPage,
  kept: ReadonlySet<string>,
): readonly ExtractedFile[] {
  return page.files.filter((_, index) => kept.has(chartItemKey("files", index)));
}

/** How many chart rows the save will write. */
export function keptCount(input: EmrPageChartImportInput): number {
  return (
    (input.medications?.length ?? 0) +
    (input.allergies?.length ?? 0) +
    (input.pmh?.length ?? 0) +
    (input.familyHistory?.length ?? 0) +
    (input.socialHistory?.length ?? 0) +
    (input.immunizations?.length ?? 0) +
    (input.labs?.accessions.reduce((total, accession) => total + accession.results.length, 0) ??
      0) +
    (input.visits?.length ?? 0)
  );
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

const PROVINCE_NAMES: Readonly<Record<string, string>> = {
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  NT: "Northwest Territories",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
};

/** What the page gives the registration form: its identity, contact and coverage fields. */
export interface RegistrationPrefill extends ExtractedDemographics {
  readonly coverage?: {
    readonly payerName: string;
    readonly memberId: string;
    readonly versionCode?: string;
    readonly expiryDate?: string;
  };
}

/**
 * The page's demographics and health card, as the registration forms take
 * them. A health card becomes the provincial plan coverage, named the way the
 * CDS importer names it ("Ontario Health Insurance Plan").
 */
export function registrationPrefill(page: ExtractedChartPage): RegistrationPrefill {
  const card = page.healthCard;
  if (card === undefined) return page.demographics;
  const province = PROVINCE_NAMES[card.province ?? "ON"];
  return {
    ...page.demographics,
    coverage: {
      payerName:
        province === undefined
          ? "Provincial Health Insurance Plan"
          : `${province} Health Insurance Plan`,
      memberId: card.number,
      // Shown as the page had them; the form's own checks flag anything it cannot take.
      ...(card.versionCode === undefined ? {} : { versionCode: card.versionCode.toUpperCase() }),
      ...(card.expiryDate === undefined ? {} : { expiryDate: card.expiryDate }),
    },
  };
}
